export type Breakpoint = '0px' | '375px' | '768px' | '1025px' | '1441px';

/**
 * @deprecated Use new string types instead
 */
export enum sizes {
    // Breakpoints shall not be changed without changing Shared/Consts/BreakpointConsts
    // Extra small screen / phone
    xs = 0,
    // Small screen / phone
    sm = 544,
    // Medium screen / tablet
    md = 768,
    // Large screen / desktop
    lg = 992,
    // Extra large screen / wide desktop
    xl = 1200,

    xxl = 1600,
    xxxl = 1921
}

export type SizesUnion = keyof typeof sizes;

type CombinedBreakpointType = Breakpoint | SizesUnion;

export type BreakPointsType<T, B extends CombinedBreakpointType = CombinedBreakpointType> = {
    [key in Extract<CombinedBreakpointType, B>]?: T;
};

export enum containerSizes {
    xl = 1230,
    xxl = 1400,
    xxxl = 1680
}

function err(): void {
    console.error('Breakpoint is invalid');
}

function next(size: sizes): sizes {
    if (!sizes[size]) {
        err();
        return null;
    }
    const entries = Object.entries(sizes).filter((value): boolean => Number.isNaN(Number(value)));
    const currentIndex = entries.findIndex(([breakpointInArray]): boolean => sizes[breakpointInArray] === size);
    const nextBreakpoint = entries[currentIndex + 1] || null;
    const [breakpoint] = nextBreakpoint;
    return nextBreakpoint ? sizes[breakpoint] : null;
}

function min(size: sizes): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    if (!size) {
        err();
        return false;
    }

    return (window as any).matchMedia(`(min-width: ${size}px)`).matches;
}

function max(size: sizes): boolean {
    if (typeof window === 'undefined') {
        return false;
    }

    if (!size) {
        err();
        return false;
    }

    const nextSize = next(size);
    if (!nextSize) {
        return true;
    }

    return (window as any).matchMedia(`(max-width: ${nextSize - 1}px)`).matches;
}

function getNameFromWidth(width: number) {
    if (width < sizes.sm) return 'xs';
    if (width < sizes.md) return 'sm';
    if (width < sizes.lg) return 'md';
    if (width < sizes.xl) return 'lg';
    if (width < sizes.xxl) return 'xl';
    if (width < sizes.xxxl) return 'xxl';
    return 'xxxl';
}

type BreakpointFunctionType = (size: sizes | number | Breakpoint) => string;
type BreakpointFunctionBetweenType = (minSize: sizes | Breakpoint, maxSize: sizes | Breakpoint) => string;

const up: BreakpointFunctionType = (size) => {
    const sizeVal = typeof size === 'string' ? Number.parseInt(size) : size;
    return `
        @media (min-width: ${sizeVal}px)
    `;
};

const down: BreakpointFunctionType = (size) => {
    const sizeVal = typeof size === 'string' ? Number.parseInt(size) : size;
    const nextSizeVal = Object.values(sizes).reduce((acc, curr, index, arr): number => {
        if (curr === sizeVal) {
            return (arr[index + 1] as number) || 0;
        }
        return acc;
    }, 0);

    if (!nextSizeVal) {
        err();
    }

    return `
            @media (max-width: ${nextSizeVal - 1}px)
        `;
};

function stripAttMedia(string: string) {
    return string.replace('@media', '');
}

const only: BreakpointFunctionType = (size) => `@media ${stripAttMedia(up(size))} and ${stripAttMedia(down(size))}`;

const between: BreakpointFunctionBetweenType = (minSize, maxSize) =>
    `@media ${stripAttMedia(up(minSize))} and ${stripAttMedia(down(maxSize))}`;

function combine(...args: string[]) {
    return `@media ${args.map(stripAttMedia).join(',')}`;
}

export const breakpoint = {
    sizes,
    getNameFromWidth,
    min,
    max,
    up,
    down,
    only,
    between,
    combine
};
